import { type SceneCategory, VideoData } from '@lumen5/luminary';

import type { ToolDefinition, ToolExecutionContext } from '../types';

import { resolveOverlayRangeOperation } from './overlay-placement';
import {
    DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME,
    isMotionGraphicsSceneRequested,
} from './motion-graphics/motionGraphicsSceneDesign';
import { computeSceneShortIds, sceneDisplayId } from './scene-addressing';
import { ListSceneDesignTool } from './ListSceneDesignTool';
import { resolveSceneGroupById, resolveSceneGroupHandle } from './scene-group-addressing';
import { validateSceneDesignId } from './scene-design-validation';
import { SetMediaTool } from './SetMediaTool';
import { SetTextTool } from './SetTextTool';
import { SplitSceneGroupTool } from './SplitSceneGroupTool';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates';
import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { createCodeoOverlayAtTranscriptRangeOperation } from 'js/src/luminary/html-asset-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveOtherOverlaysAndCreateOverlayOperation } from 'js/src/luminary/luminary-operations';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isOverlaySceneDesign,
    isSceneAnOverlay,
    isVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

interface AddVoiceoverSceneArgs {
    scene_group_id: string;
    words?: string[];
    cover_entire_video?: boolean;
    scene_design_id: string;
}

export class AddVoiceoverSceneTool extends VideoStateTool<AddVoiceoverSceneArgs> {
    public static readonly toolName = 'add_voiceover_scene';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddVoiceoverSceneTool.toolName,
                description: `Add a new scene to a voiceover or talking-head scene group, either over a specific sequence of transcript words or spanning the entire video. Word-bounded scenes start at the first given word, so they never cover leading silence — pass cover_entire_video for full-video treatments (e.g. a waveform/audiogram design) that must own the very first frame. Use ${ListSceneDesignTool.toolName} first to find a suitable design (e.g. a lower third), passing narrative_thread="talking_head" for a talking-head scene group or narrative_thread="voiceover" for a voiceover scene group. Choose ${AddVoiceoverSceneTool.toolName} when the audio structure should stay the same, the user wants emphasis on specific words, or you're adding a visual moment during speech. (To create a structural break in the narration instead, use ${SplitSceneGroupTool.toolName}.)`,
                parameters: {
                    type: 'object',
                    properties: {
                        scene_group_id: {
                            type: 'string',
                            description:
                                'The stable scene_group_id of the scene group to add the scene to (from the video state).',
                        },
                        words: {
                            type: 'array',
                            items: {
                                type: 'string',
                            },
                            description:
                                'Array of consecutive tokens from the transcript where the new scene should appear. These must appear sequentially in the transcript exactly as spelled, including any `<pause seconds="N"/>` token that falls between them (write each pause exactly as it appears in the transcript). Omit when cover_entire_video is true.',
                        },
                        cover_entire_video: {
                            type: 'boolean',
                            description:
                                'When true, the new scene spans the whole scene group — from the very first frame (including leading silence before the first word) to the end. Use instead of words ONLY for full-video treatments like an audiogram waveform design, on a scene group that has no other added scenes yet. It is rejected once the group already has added scenes because it would replace them all; to fill an uncovered range in a built-out video, pass words for just the uncovered words instead.',
                        },
                        scene_design_id: {
                            type: 'string',
                            description:
                                'The ID of the scene design to use for the new scene (e.g., "minimal:text_1")',
                        },
                    },
                    required: ['scene_group_id', 'scene_design_id'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene';
    }

    protected override onEditApplied(args: AddVoiceoverSceneArgs): void {
        // A scene added to the first scene group renders over the first scene, so it changes the
        // thumbnail. Resolve defensively — this runs after a successful edit, so it must never throw.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'layers';
    }

    /**
     * The motion-graphics branch: lay an empty graphic over the resolved words, built on the
     * template's default cutaway and then cleared. The design tool authors it afterwards.
     */
    private addMotionGraphicsScene(
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        templateId: string,
        coversEntireVideo: boolean,
        parentActionGroup: string,
    ): string {
        const cutawaySceneDesignId = getTemplateDefaultCutawaySceneDesign(
            this.getState(),
            templateId,
        );
        if (!cutawaySceneDesignId) {
            return `Error: template "${templateId}" has no default cutaway scene design, so a motion graphics scene can't be added to a voice-driven scene group.`;
        }

        const { createCodeoOverlayAtTranscriptRange } = makeOperationsCallable(
            { createCodeoOverlayAtTranscriptRangeOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const sceneIdsBefore = this.snapshotSceneIds();
        const created = createCodeoOverlayAtTranscriptRange(
            sceneGroupId,
            startTimeMillis,
            endTimeMillis,
            cutawaySceneDesignId,
        );
        if (!created) {
            return `Error: could not add a motion graphics scene to scene group ${sceneGroupId}.`;
        }

        this.startMotionGraphicsSceneBuilding(created.sceneId);
        this.blurScenesCreatedSince(sceneIdsBefore, [created.sceneId]);

        this.dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(this.luminary),
                systemTriggered: false,
                parentActionGroup,
            }),
        );

        const shortSceneId = sceneDisplayId(this.luminary, created.sceneId);
        return (
            `Success: added an empty motion graphics scene${coversEntireVideo ? ' covering the entire video' : ''} ` +
            `(scene_id ${shortSceneId}). ` +
            'It inherits the narration it covers, and the scene underneath stays as the base. It ' +
            `shows nothing yet — call ${DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME} with scene_id ` +
            `${shortSceneId} and a brief to build the graphic.`
        );
    }

    public async execute(args: AddVoiceoverSceneArgs, context: ToolExecutionContext) {
        const { words: requestedWords, cover_entire_video, scene_design_id } = args;
        const { parentActionGroup } = context;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Check if scene group has a transcript. transcriptTokens is the canonical interleaved
        // (word + pause) list the agent reads, so the words it passes back match token-for-token.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);

        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups with transcripts can have scenes added to them.`;
        }

        if (cover_entire_video && requestedWords && requestedWords.length > 0) {
            return 'Error: pass either words or cover_entire_video, not both.';
        }
        if (!cover_entire_video && (!requestedWords || requestedWords.length === 0)) {
            return 'Error: words array cannot be empty. Provide the words to cover, or set cover_entire_video: true to span the whole video.';
        }

        const currentTemplateId = this.luminary.template.id;
        const state = this.getState();

        // A motion graphics scene brings no template design to vet — it is created from the
        // default cutaway and then cleared — so the design-compatibility rules below don't apply.
        const isMotionGraphicsScene = isMotionGraphicsSceneRequested(scene_design_id);
        if (!isMotionGraphicsScene) {
            const validation = validateSceneDesignId(this.luminary, scene_design_id);
            if (validation.error !== undefined) {
                return validation.error;
            }

            // Added scenes inherit the design's category, and only voice-driven compatible
            // categories are recognized as scenes that can be added this way (and accepted
            // as base scenes by future scene creation), so reject incompatible designs
            // upfront — same rule the OverlaySelector UI applies when offering these designs.
            const designCategory: SceneCategory = validation.sceneCategory;

            const voiceDrivenDefaultSceneDesignIds = {
                defaultTalkingHeadSceneDesignId: getTemplateDefaultTalkingHeadSceneDesign(
                    state,
                    currentTemplateId,
                ),
                defaultVoiceoverSceneDesignId: getTemplateDefaultVoiceoverSceneDesign(
                    state,
                    currentTemplateId,
                ),
            };
            if (
                !isOverlaySceneDesign(
                    designCategory,
                    scene_design_id,
                    voiceDrivenDefaultSceneDesignIds,
                )
            ) {
                const isBaseDesign =
                    scene_design_id ===
                        voiceDrivenDefaultSceneDesignIds.defaultTalkingHeadSceneDesignId ||
                    scene_design_id ===
                        voiceDrivenDefaultSceneDesignIds.defaultVoiceoverSceneDesignId;
                return isBaseDesign
                    ? `Error: Scene design "${scene_design_id}" is the base scene design of voice-driven scene groups and cannot be used for an added scene. Use ${ListSceneDesignTool.toolName} and choose a different design.`
                    : `Error: Scene design "${scene_design_id}" (category "${designCategory}") cannot be used for a scene added to a voice-driven scene group. Use ${ListSceneDesignTool.toolName} and choose a design with a compatible category.`;
            }
            if (
                isVoiceoverSceneGroup(sceneGroup) &&
                !isAIAvatarSceneGroup(sceneGroup) &&
                designCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT
            ) {
                return `Error: Scene design "${scene_design_id}" is a talking-head insert design and cannot be used for a scene added to a voiceover scene group. Use ${ListSceneDesignTool.toolName} and choose a different design.`;
            }
        }

        let startTimeMillis: number;
        let endTimeMillis: number;
        if (cover_entire_video) {
            // A full-video scene spans the whole scene group, so creating one absorbs
            // every other added scene into it — silently wiping work when a scene
            // group has already been built out. cover_entire_video is meant for a
            // fresh scene group getting a single full-video treatment (e.g. an
            // audiogram/waveform design), so refuse it once other scenes have been
            // added and point back to word-bounded coverage for filling gaps.
            const existingAddedSceneCount = sceneGroup.scenes.filter((scene) =>
                isSceneAnOverlay(state, scene),
            ).length;
            if (existingAddedSceneCount > 0) {
                return `Error: cover_entire_video would replace the ${existingAddedSceneCount} scene(s) already added to this scene group with a single full-length scene. To fill an uncovered range, add a word-bounded scene covering just the uncovered words instead. cover_entire_video is only for a scene group with no other added scenes yet that should get a full-video treatment (e.g. an audiogram/waveform design).`;
            }

            const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
            if (playbackRegions.length > 0) {
                startTimeMillis = playbackRegions[0][0];
                endTimeMillis = playbackRegions[playbackRegions.length - 1][1];
            } else {
                const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                if (!sourceDuration) {
                    return `Error: Could not determine the duration of scene group ${sceneGroup.id}, so a full-video scene can't be created.`;
                }
                startTimeMillis = 0;
                endTimeMillis = sourceDuration;
            }
        } else {
            const { resolveOverlayRange } = makeOperationsCallable(
                { resolveOverlayRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            const range = resolveOverlayRange(sceneGroup, requestedWords!);
            if ('error' in range) {
                return `Error: ${range.error}`;
            }
            ({ startTimeMillis, endTimeMillis } = range);
        }

        if (isMotionGraphicsScene) {
            return this.addMotionGraphicsScene(
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                currentTemplateId,
                Boolean(cover_entire_video),
                parentActionGroup,
            );
        }

        try {
            const { moveOtherOverlaysAndCreateOverlay } = makeOperationsCallable(
                { moveOtherOverlaysAndCreateOverlayOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const sceneIdsBefore = this.snapshotSceneIds();

            const newSceneId = moveOtherOverlaysAndCreateOverlay(
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                scene_design_id,
                false, // systemTriggered
                parentActionGroup,
            );

            // Mark the new scene as "building" so the build preview blurs
            // it until the content it needs lands (set_text/set_media/image_ask).
            this.startSceneBuilding(newSceneId);
            // Creating the overlay also splits the base scene underneath it, so the
            // remainder of the scene group is a new base scene that needs blurring too.
            this.blurScenesCreatedSince(sceneIdsBefore, [newSceneId]);

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            // Report the new scene's stable scene_id (the handle the edit tools address it by) and
            // the words it covers, so the agent sets text/media on the right scene by scene_id —
            // keeping content aligned to the narration it belongs to even when it batches all the
            // adds first and fills content afterwards.
            const shortSceneId =
                computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id)).get(
                    newSceneId,
                ) ?? newSceneId;
            const coveredDescription = cover_entire_video
                ? 'the entire video'
                : `"${requestedWords!.join(' ')}"`;
            return `Success: Added scene covering ${coveredDescription} (scene_id: ${shortSceneId}). Set this scene's text/media with ${SetTextTool.toolName}/${SetMediaTool.toolName} addressing it by scene_id "${shortSceneId}". Match the content to the words it covers.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding scene: ${errorMessage}`;
        }
    }
}
